import { useEffect, useRef } from 'react';

const AppEventType = {
  CALENDAR_CONNECTED: 'CALENDAR_CONNECTED',
  MEETING_NOTE_GENERATED: 'MEETING_NOTE_GENERATED',
  MEETING_AGENDA_GENERATED: 'MEETING_AGENDA_GENERATED',
  CLIENT_CREATED: 'CLIENT_CREATED',
  DATA_UPLOADED: 'DATA_UPLOADED',
  FILE_PROCESSED: 'FILE_PROCESSED',
  SUITABILITY_REPORT_GENERATED: 'SUITABILITY_REPORT_GENERATED',
  LOADED_CALENDAR_EVENTS: 'LOADED_CALENDAR_EVENTS',
  NOTIFICATION_ACTIONED: 'NOTIFICATION_ACTIONED',
} as const;

export type AppEvent = (typeof AppEventType)[keyof typeof AppEventType];

const APP_EVENT_VALUES = new Set<string>(Object.values(AppEventType));

function isAppEvent(value: unknown): value is AppEvent {
  return typeof value === 'string' && APP_EVENT_VALUES.has(value);
}

const EVENT_NAME = 'afternoon-app-event';

export function dispatchAppEvent(event: AppEvent) {
  window.dispatchEvent(new CustomEvent<AppEvent>(EVENT_NAME, { detail: event }));
}

export function useAppEventListener(handler: (event: AppEvent) => void) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    function listener(e: Event) {
      const detail = (e as CustomEvent<unknown>).detail;
      if (isAppEvent(detail)) {
        handlerRef.current(detail);
      }
    }
    window.addEventListener(EVENT_NAME, listener);
    return () => window.removeEventListener(EVENT_NAME, listener);
  }, []);
}
